import { useState, useEffect, useRef } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { BrainCircuit, Loader2, Upload, Trash2, MessageCircle, Sparkles, CheckCircle2, AlertCircle, X, Edit3, Check, Timer, Infinity } from 'lucide-react';

const GEMINI_API_KEY = 'AIzaSyDaJs7CD3EkpmIqX1c7dJTEERICYCFdMi8';
const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-lite:generateContent?key=${GEMINI_API_KEY}`;

const AI_PROMPT = `أنت مساعد ذكي متخصص في استخراج بيانات العملاء من نصوص خام.

المطلوب منك:
1. استخراج بيانات كل عميل: الاسم الكامل، رقم الهاتف/الواتساب، البريد الإلكتروني، المستوى (beginner/intermediate/advanced)، وأي ملاحظات إضافية.
2. تنظيف أرقام الهواتف وتحويلها للصيغة الدولية:
   - الأرقام اليمنية تبدأ بـ 967 (مثال: 0771234567 → 967771234567)
   - الأرقام السعودية تبدأ بـ 966 (مثال: 0551234567 → 966551234567)
   - إذا كان الرقم يبدأ بـ 00 أو + أزل هذه البادئة واستبدلها بكود الدولة المناسب
   - إذا لم تستطع تحديد الدولة، أبقِ الرقم كما هو مع إزالة الأصفار والرموز الزائدة
3. إذا لم يُذكر المستوى صراحةً، اجعله "beginner" كقيمة افتراضية.
4. إذا لم يُذكر البريد الإلكتروني، اتركه فارغاً "".
5. أي معلومات إضافية غير مصنفة ضعها في حقل الملاحظات.

يجب أن تكون الاستجابة بصيغة JSON فقط بدون أي نص إضافي، بالشكل التالي:
{
  "customers": [
    {
      "name": "الاسم الكامل",
      "whatsapp": "رقم الهاتف بالصيغة الدولية",
      "email": "البريد الإلكتروني أو فارغ",
      "level": "beginner أو intermediate أو advanced",
      "notes": "أي ملاحظات إضافية"
    }
  ]
}

إليك النص الخام للتحليل:
`;

export default function AdminCustomerAnalysis() {
  const [rawText, setRawText] = useState('');
  const [customers, setCustomers] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState('');
  const [editingCell, setEditingCell] = useState(null); // { row, field }
  const [editValue, setEditValue] = useState('');
  const [cooldown, setCooldown] = useState(0); // seconds remaining
  const cooldownRef = useRef(null);

  // Cooldown timer effect
  useEffect(() => {
    if (cooldown > 0) {
      cooldownRef.current = setTimeout(() => {
        setCooldown(prev => prev - 1);
      }, 1000);
    } else if (cooldown === 0 && cooldownRef.current) {
      clearTimeout(cooldownRef.current);
    }
    return () => clearTimeout(cooldownRef.current);
  }, [cooldown]);

  // Analyze with Gemini AI
  const handleAnalyze = async () => {
    if (!rawText.trim()) {
      setError('الرجاء إدخال بيانات في مساحة النص أولاً.');
      return;
    }

    setIsAnalyzing(true);
    setError('');
    setCustomers([]);
    setUploadSuccess(false);

    try {
      const response = await fetch(GEMINI_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: AI_PROMPT + rawText }] }],
          generationConfig: {
            temperature: 0.1,
            topP: 0.8,
            maxOutputTokens: 8192,
          }
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          setCooldown(30);
          throw new Error('⏳ تم تجاوز حد الاستخدام. برجاء الانتظار 30 ثانية ثم المحاولة مرة أخرى.');
        }
        if (response.status === 403) {
          throw new Error('🔑 مفتاح الـ API لا يملك صلاحية لهذا النموذج. تحقق من إعدادات المفتاح في Google AI Studio.');
        }
        throw new Error(errData?.error?.message || `خطأ في الاتصال بالذكاء الاصطناعي (${response.status})`);
      }

      const data = await response.json();
      const textContent = data?.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!textContent) {
        throw new Error('لم يتم الحصول على استجابة من الذكاء الاصطناعي.');
      }

      // Extract JSON from response (handle markdown code blocks)
      let jsonStr = textContent;
      const jsonMatch = textContent.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (jsonMatch) {
        jsonStr = jsonMatch[1];
      }

      const parsed = JSON.parse(jsonStr.trim());

      if (!parsed.customers || !Array.isArray(parsed.customers) || parsed.customers.length === 0) {
        throw new Error('لم يتمكن الذكاء الاصطناعي من العثور على أي بيانات عملاء في النص.');
      }

      setCustomers(parsed.customers);
    } catch (err) {
      console.error('Gemini API Error:', err);
      if (err instanceof SyntaxError) {
        setError('تعذر تحليل استجابة الذكاء الاصطناعي. حاول مرة أخرى.');
      } else {
        setError(err.message || 'حدث خطأ غير متوقع أثناء التحليل.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Upload to Firestore
  const handleUpload = async () => {
    if (customers.length === 0) return;

    setIsUploading(true);
    setError('');

    try {
      const batch = customers.map(customer =>
        addDoc(collection(db, 'customers'), {
          name: customer.name || '',
          whatsapp: customer.whatsapp || '',
          email: customer.email || '',
          level: customer.level || 'beginner',
          notes: customer.notes || '',
          status: 'interested',
          source: 'ai-analysis',
          amountPaid: 0,
          createdAt: serverTimestamp(),
        })
      );

      await Promise.all(batch);
      setUploadSuccess(true);
      // Clear after 3 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 4000);
    } catch (err) {
      console.error('Firestore upload error:', err);
      setError('حدث خطأ أثناء رفع البيانات إلى قاعدة البيانات. تحقق من الاتصال وحاول مرة أخرى.');
    } finally {
      setIsUploading(false);
    }
  };

  // Delete a row
  const handleDeleteRow = (index) => {
    setCustomers(prev => prev.filter((_, i) => i !== index));
  };

  // Start editing
  const startEditing = (rowIndex, field, currentValue) => {
    setEditingCell({ row: rowIndex, field });
    setEditValue(currentValue || '');
  };

  // Save edit
  const saveEdit = () => {
    if (!editingCell) return;
    setCustomers(prev => prev.map((c, i) =>
      i === editingCell.row ? { ...c, [editingCell.field]: editValue } : c
    ));
    setEditingCell(null);
    setEditValue('');
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Level badge
  const getLevelBadge = (level) => {
    const map = {
      beginner: { label: 'مبتدئ', cls: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400' },
      intermediate: { label: 'متوسط', cls: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' },
      advanced: { label: 'متقدم', cls: 'bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400' },
    };
    const info = map[level] || map.beginner;
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-md font-bold ${info.cls}`}>{info.label}</span>;
  };

  // Reset all
  const handleReset = () => {
    setRawText('');
    setCustomers([]);
    setError('');
    setUploadSuccess(false);
    setEditingCell(null);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white shadow-lg shadow-primary-500/25">
              <BrainCircuit className="w-5 h-5" />
            </div>
            تحليل بيانات العملاء
          </h1>
          <p className="text-slate-500 dark:text-zinc-400">الصق البيانات الخام واترك الذكاء الاصطناعي يستخرج ويُنظّم معلومات العملاء تلقائياً.</p>
        </div>

        {customers.length > 0 && (
          <button
            onClick={handleReset}
            className="flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 px-5 py-2.5 rounded-xl font-bold transition-all"
          >
            <X className="w-4 h-4" />
            تحليل جديد
          </button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-2xl p-4 flex items-start gap-3 animate-in fade-in duration-300">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-red-700 dark:text-red-400 font-bold text-md">{error}</p>
          </div>
          <button onClick={() => setError('')} className="mr-auto text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Upload Success Alert */}
      {uploadSuccess && (
        <div className="mb-6 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-2xl p-4 flex items-center gap-3 animate-in fade-in duration-300">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
          <p className="text-emerald-700 dark:text-emerald-400 font-bold text-md">
            تم رفع {customers.length} عميل بنجاح إلى قاعدة البيانات! 🎉
          </p>
        </div>
      )}

      {/* Input Section */}
      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-sm mb-6">
        <div className="p-4 md:p-6 border-b border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-zinc-900/50">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-500" />
            البيانات الخام
          </h2>
          <p className="text-mdtext-slate-500 dark:text-zinc-400 mt-1">
            الصق هنا بيانات العملاء من أي مصدر (واتساب، إكسل، ملاحظات...) وسيقوم الذكاء الاصطناعي بتنظيمها.
          </p>
        </div>
        <div className="p-4 md:p-6">
          <textarea
            id="raw-data-input"
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            rows="10"
            className="w-full px-4 py-4 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all text-slate-900 dark:text-white resize-none font-medium leading-relaxed placeholder:text-slate-400 dark:placeholder:text-zinc-500"
            placeholder={`مثال:\nمحمد أحمد - 0771234567 - مبتدئ - يريد تعلم البرمجة\nسارة علي - 0551234567 - sara@email.com - متوسط\nأحمد خالد - 00967712345678 - متقدم - لديه خبرة سابقة`}
            dir="rtl"
          />

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              id="analyze-btn"
              onClick={handleAnalyze}
              disabled={isAnalyzing || !rawText.trim() || cooldown > 0}
              className="flex items-center justify-center gap-2.5 bg-gradient-to-l from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white px-6 py-3 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>جاري التحليل...</span>
                </>
              ) : cooldown > 0 ? (
                <>
                  <Timer className="w-5 h-5" />
                  <span>انتظر {cooldown} ثانية...</span>
                </>
              ) : (
                <>
                  <BrainCircuit className="w-5 h-5" />
                  <span>تحليل بالذكاء الاصطناعي</span>
                </>
              )}
            </button>

            {rawText.trim() && (
              <span className="text-mdtext-slate-400 dark:text-zinc-500 font-medium">
                {rawText.trim().split('\n').filter(l => l.trim()).length} سطر مُدخل
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Analyzing Skeleton */}
      {isAnalyzing && (
        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-sm mb-6">
          <div className="p-8 md:p-12 flex flex-col items-center justify-center text-center">
            <div className="relative mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white shadow-xl shadow-primary-500/30 animate-pulse">
                <BrainCircuit className="w-8 h-8" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 animate-ping"></div>
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">جاري تحليل البيانات...</h3>
            <p className="text-slate-500 dark:text-zinc-400 text-mdmax-w-md">
              الذكاء الاصطناعي يقوم باستخراج معلومات العملاء، تنظيف الأرقام، وتحويلها للصيغة الدولية. قد يستغرق هذا بضع ثوانٍ.
            </p>
            <div className="mt-6 flex gap-1">
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className="w-2 h-2 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }}></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      {customers.length > 0 && !isAnalyzing && (
        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-sm">
          <div className="p-4 md:p-6 border-b border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-zinc-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                النتائج المستخرجة
              </h2>
              <p className="text-mdtext-slate-500 dark:text-zinc-400 mt-1">
                تم استخراج <span className="text-primary-600 dark:text-primary-400 font-bold">{customers.length}</span> عميل — راجع البيانات وعدّلها قبل الرفع.
              </p>
            </div>

            <button
              id="upload-btn"
              onClick={handleUpload}
              disabled={isUploading || uploadSuccess}
              className={`flex items-center justify-center gap-2.5 px-6 py-3 rounded-xl font-bold transition-all shadow-sm ${uploadSuccess
                ? 'bg-emerald-500 text-white cursor-default'
                : 'bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 text-white disabled:opacity-50'
                }`}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>جاري الرفع...</span>
                </>
              ) : uploadSuccess ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <span>تم الرفع بنجاح</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  <span>تأكيد الرفع إلى Firebase</span>
                </>
              )}
            </button>
          </div>

          {/* Data Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-zinc-900 border-b border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 text-mdfont-bold">
                  <th className="px-6 py-4 whitespace-nowrap w-8">#</th>
                  <th className="px-6 py-4 whitespace-nowrap">الاسم</th>
                  <th className="px-6 py-4 whitespace-nowrap">رقم الواتساب</th>
                  <th className="px-6 py-4 whitespace-nowrap">البريد الإلكتروني</th>
                  <th className="px-6 py-4 whitespace-nowrap">المستوى</th>
                  <th className="px-6 py-4 min-w-[180px]">الملاحظات</th>
                  <th className="px-6 py-4 whitespace-nowrap text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {customers.map((customer, index) => (
                  <tr key={index} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors group">
                    {/* Row Number */}
                    <td className="px-6 py-5 text-mdtext-slate-400 dark:text-zinc-500 font-bold">{index + 1}</td>

                    {/* Name */}
                    <td className="px-6 py-5 font-bold text-slate-900 dark:text-white whitespace-nowrap">
                      {editingCell?.row === index && editingCell?.field === 'name' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' ? saveEdit() : e.key === 'Escape' && cancelEdit()}
                            className="px-3 py-1.5 bg-white dark:bg-black/50 border border-primary-500 rounded-lg outline-none text-mdfont-bold w-full"
                            autoFocus
                          />
                          <button onClick={saveEdit} className="text-emerald-500 hover:text-emerald-600"><Check className="w-4 h-4" /></button>
                          <button onClick={cancelEdit} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <span
                          className="cursor-pointer hover:text-primary-600 transition-colors"
                          onClick={() => startEditing(index, 'name', customer.name)}
                          title="انقر للتعديل"
                        >
                          {customer.name || '-'}
                        </span>
                      )}
                    </td>

                    {/* WhatsApp */}
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {editingCell?.row === index && editingCell?.field === 'whatsapp' ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' ? saveEdit() : e.key === 'Escape' && cancelEdit()}
                              className="px-3 py-1.5 bg-white dark:bg-black/50 border border-primary-500 rounded-lg outline-none text-mdfont-bold w-36"
                              dir="ltr"
                              autoFocus
                            />
                            <button onClick={saveEdit} className="text-emerald-500 hover:text-emerald-600"><Check className="w-4 h-4" /></button>
                            <button onClick={cancelEdit} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                          </div>
                        ) : (
                          <>
                            <span
                              className="text-slate-600 dark:text-zinc-300 font-medium font-sans cursor-pointer hover:text-primary-600 transition-colors"
                              dir="ltr"
                              onClick={() => startEditing(index, 'whatsapp', customer.whatsapp)}
                              title="انقر للتعديل"
                            >
                              +{(customer.whatsapp || '').replace(/\D/g, '')}
                            </span>
                            <a
                              href={`https://wa.me/${(customer.whatsapp || '').replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 text-emerald-500 hover:text-emerald-600 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20 rounded-lg transition-colors"
                              title="فتح محادثة واتساب"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </a>
                          </>
                        )}
                      </div>
                    </td>

                    {/* Email */}
                    <td className="px-6 py-5 whitespace-nowrap">
                      {editingCell?.row === index && editingCell?.field === 'email' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="email"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' ? saveEdit() : e.key === 'Escape' && cancelEdit()}
                            className="px-3 py-1.5 bg-white dark:bg-black/50 border border-primary-500 rounded-lg outline-none text-mdfont-bold w-44"
                            dir="ltr"
                            autoFocus
                          />
                          <button onClick={saveEdit} className="text-emerald-500 hover:text-emerald-600"><Check className="w-4 h-4" /></button>
                          <button onClick={cancelEdit} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        </div>
                      ) : customer.email ? (
                        <span
                          className="text-slate-600 dark:text-zinc-300 font-medium font-sans cursor-pointer hover:text-primary-600 transition-colors"
                          dir="ltr"
                          onClick={() => startEditing(index, 'email', customer.email)}
                          title="انقر للتعديل"
                        >
                          {customer.email}
                        </span>
                      ) : (
                        <span
                          className="text-slate-400 dark:text-zinc-500 italic text-mdcursor-pointer hover:text-primary-500"
                          onClick={() => startEditing(index, 'email', '')}
                        >
                          أضف إيميل
                        </span>
                      )}
                    </td>

                    {/* Level */}
                    <td className="px-6 py-5 whitespace-nowrap">
                      <select
                        value={customer.level || 'beginner'}
                        onChange={(e) => {
                          setCustomers(prev => prev.map((c, i) =>
                            i === index ? { ...c, level: e.target.value } : c
                          ));
                        }}
                        className="text-md font-bold rounded-full px-3 py-1.5 border outline-none transition-colors cursor-pointer appearance-none bg-slate-50 dark:bg-black/30 border-slate-200 dark:border-white/10 text-slate-700 dark:text-zinc-300"
                        style={{ textAlignLast: 'center' }}
                      >
                        <option value="beginner">مبتدئ</option>
                        <option value="intermediate">متوسط</option>
                        <option value="advanced">متقدم</option>
                      </select>
                    </td>

                    {/* Notes */}
                    <td className="px-6 py-5">
                      {editingCell?.row === index && editingCell?.field === 'notes' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' ? saveEdit() : e.key === 'Escape' && cancelEdit()}
                            className="px-3 py-1.5 bg-white dark:bg-black/50 border border-primary-500 rounded-lg outline-none text-mdfont-bold w-full"
                            autoFocus
                          />
                          <button onClick={saveEdit} className="text-emerald-500 hover:text-emerald-600"><Check className="w-4 h-4" /></button>
                          <button onClick={cancelEdit} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <span
                          className={`max-w-[200px] truncate block cursor-pointer ${customer.notes ? 'text-slate-700 dark:text-zinc-300 font-medium' : 'text-slate-400 dark:text-zinc-500 text-mditalic'}`}
                          onClick={() => startEditing(index, 'notes', customer.notes)}
                          title={customer.notes || 'انقر لإضافة ملاحظة'}
                        >
                          {customer.notes || 'أضف ملاحظة...'}
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleDeleteRow(index)}
                          className="p-2 text-slate-400 hover:text-red-600 bg-slate-100 hover:bg-red-50 dark:bg-white/5 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                          title="حذف هذا العميل"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer Stats */}
          <div className="p-4 md:p-6 border-t border-slate-200 dark:border-white/10 bg-slate-50/50 dark:bg-zinc-900/50 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-4 text-mdfont-bold">
              <span className="text-slate-500 dark:text-zinc-400">
                الإجمالي: <span className="text-slate-900 dark:text-white">{customers.length}</span> عميل
              </span>
              <span className="text-slate-500 dark:text-zinc-400">
                مبتدئ: <span className="text-blue-600 dark:text-blue-400">{customers.filter(c => c.level === 'beginner' || !c.level).length}</span>
              </span>
              <span className="text-slate-500 dark:text-zinc-400">
                متوسط: <span className="text-amber-600 dark:text-amber-400">{customers.filter(c => c.level === 'intermediate').length}</span>
              </span>
              <span className="text-slate-500 dark:text-zinc-400">
                متقدم: <span className="text-purple-600 dark:text-purple-400">{customers.filter(c => c.level === 'advanced').length}</span>
              </span>
            </div>

            <p className="text-md text-slate-400 dark:text-zinc-500 flex items-center gap-1">
              <Edit3 className="w-3 h-3" />
              انقر على أي خلية لتعديلها مباشرة
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
